import {
  BarChart3Icon,
  CreditCardIcon,
  LandmarkIcon,
  ReceiptIcon,
  UsersIcon,
  BoxesIcon,
  WalletIcon,
  ZapIcon,
} from 'lucide-react'
import type { ProductPageContent } from '../../types/product'

export const salesPos: ProductPageContent = {
  slug: 'sales-pos',
  name: 'Point of Sale',
  icon: CreditCardIcon,
  hero: {
    headline: 'Sell faster and get paid every way your customers pay',
    subheadline:
      'Ring up sales in seconds, accept mobile money, card, cash, or bank transfer, and issue EBM receipts automatically — from one simple till.',
    trustLine: 'RRA EBM certified · Used by 8,000+ businesses',
    visual: { kind: 'screen', id: 'pos-hero' },
  },
  stats: [
    { value: '8,000+', label: 'businesses selling with Kayko every day' },
    { value: 'RWF 13B+', label: 'in sales recorded through Kayko each month' },
    { value: '9 sec', label: 'average time to complete a checkout' },
  ],
  benefits: {
    heading: 'A till that keeps up with your busiest hour.',
    items: [
      {
        icon: ZapIcon,
        title: 'Faster checkout',
        copy: 'Search, scan, or tap products and take payment in a few seconds, even when the queue is long.',
      },
      {
        icon: WalletIcon,
        title: 'Every payment method',
        copy: 'Accept mobile money, card, cash, and bank transfer — or split one bill across several.',
      },
      {
        icon: ReceiptIcon,
        title: 'EBM-compliant receipts',
        copy: 'Every sale produces an RRA-compliant receipt automatically, printed or sent by SMS.',
      },
    ],
  },
  features: {
    heading: 'Everything you need at the counter.',
    subheading: 'Built for shops, restaurants, hotels, and clinics that serve customers all day.',
    items: [
      {
        title: 'Checkout and receipts in seconds',
        copy: 'Add items by search, barcode, or favourites and complete the sale in a couple of taps. Receipts are generated through EBM and printed or sent to the customer’s phone.',
        bullets: [
          'Barcode scanning and quick-tap favourites',
          'Print receipts or send them by SMS',
          'Hold and resume orders when the counter is busy',
        ],
        visual: { kind: 'screen', id: 'checkout' },
      },
      {
        title: 'Get paid however customers want to pay',
        copy: 'Take mobile money, card, cash, and bank transfer from the same screen. Every payment is matched to its sale, so your cash-up is ready at closing time.',
        bullets: [
          'Mobile money requests sent straight to the customer’s phone',
          'Split one bill across several payment methods',
          'Automatic end-of-day cash-up by payment type',
        ],
        visual: { kind: 'screen', id: 'payments' },
      },
      {
        title: 'Discounts and returns without the paperwork',
        copy: 'Apply discounts by item, by bill, or with a promotion you set up in advance. Process returns and refunds against the original receipt, with stock updated automatically.',
        bullets: [
          'Percentage, fixed amount, and time-based promotions',
          'Refunds linked to the original EBM receipt',
          'Manager approval for discounts above your limit',
        ],
        visual: { kind: 'screen', id: 'returns' },
      },
      {
        title: 'See sales by staff and location',
        copy: 'Know who sold what, at which branch, and at what time of day. Compare locations side by side and spot your strongest hours and people.',
        bullets: [
          'Individual staff logins with their own permissions',
          'Sales and payments broken down by branch',
          'Live totals you can check from your phone',
        ],
        visual: { kind: 'screen', id: 'staff-sales' },
      },
    ],
  },
  steps: {
    heading: 'Up and running in an afternoon.',
    items: [
      {
        title: 'Add your products',
        copy: 'Import your price list or add items one by one with prices, barcodes, and stock levels.',
      },
      {
        title: 'Start selling',
        copy: 'Open the till on a phone, tablet, or computer and take your first payment the same day.',
      },
      {
        title: 'See your sales live',
        copy: 'Watch totals, top products, and payments update in real time from anywhere.',
      },
    ],
  },
  related: {
    heading: 'Works with the rest of Kayko.',
    subheading: 'Every sale flows straight into the rest of your business.',
    items: [
      {
        icon: BoxesIcon,
        title: 'Inventory',
        copy: 'Stock levels update with every sale, so you know when to reorder.',
        href: '/products/inventory',
      },
      {
        icon: UsersIcon,
        title: 'Customers',
        copy: 'Keep purchase history, balances, and credit for every regular.',
        href: '/products/customers',
      },
      {
        icon: BarChart3Icon,
        title: 'Reports & Insights',
        copy: 'Turn daily sales into clear profit and cash flow reports.',
        href: '/products/reports',
      },
      {
        icon: LandmarkIcon,
        title: 'Business Financing',
        copy: 'Your sales history helps you check eligibility for working capital.',
        href: '/products/business-finance',
      },
    ],
  },
  industries: {
    heading: 'Built for your industry.',
    subheading: 'Point of Sale adapts to the way your business serves customers.',
    items: [
      {
        title: 'Retail',
        copy: 'Scan, sell, and keep shelves stocked.',
        image:
          'https://cdn.magicpatterns.com/uploads/hrSxrDybaWkqRaXzBR7V3y/ChatGPT_Image_Sep_26,_2026,_04_09_02_PM-1.png',
        alt: 'Shopkeeper arranging products on shelves in a busy retail store',
        href: '/solutions/retail',
      },
      {
        title: 'Food & Beverage',
        copy: 'Take orders by table and split bills easily.',
        image:
          'https://cdn.magicpatterns.com/uploads/um4PVUwRMuB6Dq6iSx7Up4/ChatGPT_Image_Sep_26,_2026,_04_09_05_PM-4.png',
        alt: 'Chef plating food beside an order screen in a restaurant kitchen',
        href: '/solutions/food-beverage',
      },
      {
        title: 'Hospitality',
        copy: 'Charge rooms, bars, and restaurants in one place.',
        image:
          'https://cdn.magicpatterns.com/uploads/kif38iqadZcEesbQLKpsTR/ChatGPT_Image_Sep_26,_2026,_04_09_03_PM-2.png',
        alt: 'Receptionist welcoming a guest at a boutique hotel front desk',
        href: '/solutions/hospitality',
      },
      {
        title: 'Healthcare',
        copy: 'Bill pharmacy sales and clinic services together.',
        image:
          'https://cdn.magicpatterns.com/uploads/iDUc4UaB1F5bDm8tDia9dE/ChatGPT_Image_Sep_26,_2026,_04_09_04_PM-3.png',
        alt: 'Pharmacist serving a customer at a neighborhood pharmacy counter',
        href: '/solutions/healthcare',
      },
    ],
  },
  testimonial: {
    quote:
      'Before Kayko, the morning rush meant long queues and handwritten receipts. Now my team serves customers in seconds, every payment is recorded, and I can check the day’s sales from home.',
    name: 'Jean-Paul Niyonzima',
    role: 'Owner',
    business: 'Mizero Café & Bakery',
    location: 'Kimihurura, Kigali',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/2c23e8d6-e530-4eee-9f47-5e53b680c437.jpg',
    metric: { value: '+34%', label: 'more customers served per day in six months' },
  },
  pricingTeaser: {
    text: 'Point of Sale is included in every Kayko plan.',
    linkLabel: 'See pricing',
    href: '#',
  },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'What hardware do I need?',
        answer:
          'You can start with the phone, tablet, or computer you already have. Kayko also works with receipt printers, barcode scanners, and cash drawers, and we can recommend compatible devices available in Kigali.',
      },
      {
        question: 'Can I keep selling when the internet goes down?',
        answer:
          'Yes. The till keeps working offline and saves every sale on your device. As soon as you reconnect, sales, payments, and receipts sync automatically.',
      },
      {
        question: 'Are Kayko receipts accepted by RRA?',
        answer:
          'Yes. Kayko is RRA EBM certified, so every sale generates a compliant electronic receipt with the correct signature and receipt number.',
      },
      {
        question: 'Are there fees on payments?',
        answer:
          'Recording cash and bank transfer sales is free. Mobile money and card payments carry the standard processing fee from the payment provider, shown clearly before you start accepting them.',
      },
      {
        question: 'How many staff can use the till?',
        answer:
          'As many as you need. Each staff member gets their own login and permissions, so you can see who made every sale, discount, and refund.',
      },
      {
        question: 'Can I move over from my current system?',
        answer:
          'Yes. Upload your products, prices, and customer list from a spreadsheet, and our Kigali team will help you set up — usually in less than a day.',
      },
    ],
  },
  cta: {
    headline: 'Start selling with Kayko today.',
    subheadline: 'Set up your till in minutes. No long contracts, and support from our team in Kigali.',
  },
}
